import {
  Box,
  Button,
  ButtonText,
  Heading,
  HStack,
  Text,
  VStack,
} from '@gluestack-ui/themed';
import {
  useCallback, useLayoutEffect, useMemo, useRef,
} from 'react';

import '@/common/i18n';
import { useTenantSettingsStore } from '@/common/context/TenantSettingsStore';
import EvaluationCard from '@/student/task/components/aiTutor/EvaluationCard';
import { useTaskSubmissionStore } from '@/student/task/stores/TaskSubmissionStore';
import {
  EVAL_CARD_LAB_DIMENSIONS,
  EVAL_CARD_LAB_EVALUATED_AT,
  EVAL_CARD_LAB_GRADING_SETTINGS,
  EVAL_CARD_LAB_MIN_LEVELS,
  EVAL_CARD_LAB_STATE_FIXTURES,
  EVAL_CARD_LAB_STATES,
  type EvalCardLabState,
} from '@/visual-lab/chapters/eval-card/evalCardLabFixture';
import {
  buildEvalCardLabSearch,
  EVAL_CARD_LAB_DRAWER_WIDTHS,
  parseEvalCardLabQuery,
  type EvalCardLabQueryState,
} from '@/visual-lab/chapters/eval-card/parseEvalCardLabQuery';

const STATE_LABELS: Record<EvalCardLabState, string> = {
  'not-ready': 'Not ready',
  ready: 'Ready',
  edited: 'Edited after Ready',
};

/** Matches the AI Tutor drawer header height in `AssistanceDrawer`. */
const DRAWER_HEADER_HEIGHT = 72;

type EvalCardLabScreenProps = {
  search: string;
  onNavigate: (path: string) => void;
};

function seedEvalCardLabStores(state: EvalCardLabState) {
  const current = useTenantSettingsStore.getState().settings;
  useTenantSettingsStore.setState({
    settings: {
      ...current,
      gradingSettings: {
        ...current?.gradingSettings,
        ...EVAL_CARD_LAB_GRADING_SETTINGS,
      },
    },
    status: 'success',
  });
  const fixture = EVAL_CARD_LAB_STATE_FIXTURES[state];
  const store = useTaskSubmissionStore.getState();
  useTaskSubmissionStore.setState({
    submission: { ...store.submission, lastSavedAt: fixture.lastSavedAt },
    evaluation: {
      ...store.evaluation,
      isEvaluating: false,
      data: fixture.evaluation,
      expectedDims: [...EVAL_CARD_LAB_DIMENSIONS],
      evaluatedAt: EVAL_CARD_LAB_EVALUATED_AT,
      completionPolicy: 'gated',
      minLevelsEffective: EVAL_CARD_LAB_MIN_LEVELS,
      readiness: null,
    },
  });
  useTaskSubmissionStore.getState().recomputeCanSubmit();
}

function ControlGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <VStack space="xs">
      <Text size="xs" color="$textHint" fontWeight="semibold" letterSpacing="$sm" textTransform="uppercase">
        {label}
      </Text>
      <HStack space="xs" flexWrap="wrap">
        {children}
      </HStack>
    </VStack>
  );
}

function ToggleChip({
  active,
  label,
  onPress,
  testID,
}: {
  active: boolean;
  label: string;
  onPress: () => void;
  testID?: string;
}) {
  return (
    <Button
      onPress={onPress}
      size="sm"
      variant={active ? 'solid' : 'outline'}
      action={active ? 'primary' : 'secondary'}
      testID={testID}
    >
      <ButtonText>{label}</ButtonText>
    </Button>
  );
}

/**
 * Eval Card Lab — the student evaluation card on a gated draft, inside a drawer-width frame.
 */
export function EvalCardLabScreen({ search, onNavigate }: EvalCardLabScreenProps) {
  const query = useMemo(() => parseEvalCardLabQuery(search), [search]);

  const seededState = useRef<EvalCardLabState | null>(null);
  if (seededState.current === null) {
    seededState.current = query.state;
    seedEvalCardLabStores(query.state);
  }
  useLayoutEffect(() => {
    if (seededState.current === query.state) return;
    seededState.current = query.state;
    seedEvalCardLabStores(query.state);
  }, [query.state]);

  const syncQuery = useCallback((patch: Partial<EvalCardLabQueryState>) => {
    onNavigate(`/visual-lab/eval-card${buildEvalCardLabSearch({ ...query, ...patch })}`);
  }, [onNavigate, query]);

  return (
    <Box flex={1} width="100%" bg="$bgApp">
      <HStack
        space="4xl"
        px="$8"
        py="$8"
        maxWidth={1040}
        alignSelf="center"
        width="100%"
        flexWrap="wrap"
        alignItems="flex-start"
      >
        <VStack space="xl" flex={1} minWidth={280} maxWidth={440}>
          <VStack space="xs">
            <Heading size="xl">Eval Card Lab</Heading>
            <Text size="sm" color="$textSubtle">
              The student evaluation card on a draft that must pass before Submit, at the real AI Tutor
              drawer width. Formal and Description need Good, Analysis needs Very Good, Application has no
              requirement.
            </Text>
          </VStack>

          <ControlGroup label="Draft">
            {EVAL_CARD_LAB_STATES.map((option) => (
              <ToggleChip
                key={option}
                label={STATE_LABELS[option]}
                active={query.state === option}
                testID={`eval-card-lab-state-${option}`}
                onPress={() => syncQuery({ state: option })}
              />
            ))}
          </ControlGroup>

          <ControlGroup label="Drawer width">
            {EVAL_CARD_LAB_DRAWER_WIDTHS.map((option) => (
              <ToggleChip
                key={option}
                label={`${option}px`}
                active={query.width === option}
                testID={`eval-card-lab-width-${option}`}
                onPress={() => syncQuery({ width: option })}
              />
            ))}
          </ControlGroup>
        </VStack>

        <Box
          width={query.width}
          bg="$bgCard"
          borderWidth={1}
          borderColor="$borderLight"
          borderRadius="$xl"
          overflow="hidden"
          testID="eval-card-lab-drawer"
        >
          <HStack
            h={DRAWER_HEADER_HEIGHT}
            alignItems="center"
            px="$6"
            borderBottomWidth={1}
            borderColor="$borderLight"
          >
            <Heading size="sm" color="$textHeading">AI Tutor</Heading>
          </HStack>
          <Box p="$6" pb="$4" testID="eval-card-lab-preview">
            <EvaluationCard isMarkedAsDone={false} gated onSubmit={() => {}} />
          </Box>
        </Box>
      </HStack>
    </Box>
  );
}
